import { ChangeEventHandler } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { AlertOptions } from 'contexts/alert/AlertContext';
import type { ContentItem } from 'contexts/datamodel/DataModelContext';
import {
  ArticleItem,
  BannerModuleContainer,
  ContentUnion,
  ExternalCollectionItem,
  ModuleContainer,
  ModuleContainerUnion,
  MultiZoneModuleContainer,
  OffPlatformListContainer,
  PageModule,
  PublicationSettingSearchableContentType,
  QueryItem,
  TreatmentTypeSetting,
  UiBasicModule,
  UiBasicOptionalModule,
  UiBasicOptionalTreatmentType,
  UiCollectionModule,
  UiCollectionTreatmentType,
  UiFeedsModule,
  UiFeedsTreatmentType,
  UiModule,
  UiModuleType,
  UiRankedModule,
  UiRankedTreatmentType,
  UiTextModule,
  UiTextTreatmentType,
  UiTitleModule,
  UiTitlePinnedModule,
  UiTitleTreatmentType
} from 'data/generated/graphql';
import { AllessehContent, AllessehContentQueryBody, AllessehQueryRule } from 'hooks/useAllessehContentQuery';
import { SECTION_NAME_OVERRIDE } from 'utils/constants';
import { isNullOrUndefined, safelyParseContent } from './temp';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

export const UNLABELED_MODULE = 'UNLABELED';

export const getEffectiveItemLimits = (
  pageModule: PageModule,
  setting: TreatmentTypeSetting | null | undefined
): { min: number | undefined; max: number | undefined; hasLimitOverride: boolean } => ({
  min: pageModule.minItemsOverride ?? setting?.minRequiredContentItems ?? undefined,
  max: pageModule.maxItemsOverride ?? setting?.maxRequiredContentItems ?? undefined,
  hasLimitOverride: !isNullOrUndefined(pageModule.minItemsOverride) || !isNullOrUndefined(pageModule.maxItemsOverride)
});
export const isJWSJOpinion = (section?: string | null) =>
  section ? ['j-opinion', 'j-shasetsu', 'j-kiko'].includes(section.toLowerCase()) : false;

export const getUiModuleTypeLabel = (uiModuleType: UiModuleType) =>
  `${uiModuleType
    .replaceAll('ModuleType', '')
    .replaceAll('Ui', '')
    .replace(/([A-Z])/g, ' $1')
    .trim()}`;

export function getModuleFieldKey(uiModuleType: UiModuleType) {
  switch (uiModuleType) {
    case UiModuleType.UiBasicModuleType:
      return 'basicModule';
    case UiModuleType.UiRankedModuleType:
      return 'rankedModule';
    case UiModuleType.UiTitleModuleType:
      return 'titleModule';
    case UiModuleType.UiTextModuleType:
      return 'textModule';
    case UiModuleType.UiBasicOptionalModuleType:
      return 'basicOptionalModule';
    case UiModuleType.UiTitlePinnedModuleType:
      return 'titlePinnedModule';
    case UiModuleType.UiFeedsModuleType:
      return 'feedsModule';
    case UiModuleType.UiCollectionModuleType:
      return 'collectionModule';
    default:
      return 'twoColumnModule';
  }
}

export const findTreatmentSetting = <T extends { treatmentType: string; treatmentTypeKey?: string | null }>(
  settings: T[],
  typeOrKey: string | null | undefined
): T | undefined => {
  if (!typeOrKey) return undefined;
  return settings.find((s) => s.treatmentType === typeOrKey || s.treatmentTypeKey === typeOrKey);
};

export const getTreatmentType = (pageModule: PageModule): string | null => {
  if (!pageModule.uiModuleFields) return null;
  let treatmentType: string | null = null;
  const keys = Object.keys(pageModule.uiModuleFields);
  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    const uiModuleFieldKey = key as keyof UiModule;
    const uiModule = pageModule.uiModuleFields[uiModuleFieldKey] as { treatmentType: string } | null;
    if (uiModule) {
      treatmentType = uiModule.treatmentType;
    }
  }
  return treatmentType;
};

export function getModuleLabel(module: ModuleContainerUnion, treatmentTypeSettings: TreatmentTypeSetting[]): string {
  if (!module.attributes.pageModule) return UNLABELED_MODULE;

  const moduleType = module.attributes.pageModule.uiModuleType;
  const moduleTreatmentType = getTreatmentType(module.attributes.pageModule);

  const treatmentTypes = treatmentTypeSettings.reduce<Record<string, TreatmentTypeSetting>>((acc, setting) => {
    acc[setting.treatmentType] = setting;
    if (setting.treatmentTypeKey) acc[setting.treatmentTypeKey] = setting;
    return acc;
  }, {});

  const treatmentTypeLabel =
    (moduleTreatmentType != null
      ? (treatmentTypes as Record<string, TreatmentTypeSetting | undefined>)[moduleTreatmentType]?.label
      : null) ?? UNLABELED_MODULE;

  switch (moduleType) {
    case UiModuleType.UiBasicModuleType:
    case UiModuleType.UiTitleModuleType:
      return treatmentTypeLabel;
    case UiModuleType.UiRankedModuleType:
      return module.attributes.pageModule.uiModuleFields?.rankedModule?.title ?? treatmentTypeLabel;
    case UiModuleType.UiTextModuleType:
      return module.attributes.pageModule.uiModuleFields?.textModule?.mainText ?? treatmentTypeLabel;
    case UiModuleType.UiBasicOptionalModuleType:
      return treatmentTypeLabel;
    case UiModuleType.UiTitlePinnedModuleType:
      return module.attributes.pageModule.uiModuleFields?.titlePinnedModule?.title ?? treatmentTypeLabel;
    case UiModuleType.UiFeedsModuleType:
      return 'UiFeedsModuleType';
    default:
      return treatmentTypeLabel;
  }
}

export function getSubsetTreatmentTypeSettings<T extends { treatmentType: string; treatmentTypeKey?: string | null }>(
  uiModuleType: UiModuleType,
  treatmentTypeSettings: T[] = []
): T[] {
  switch (uiModuleType) {
    case UiModuleType.UiBasicModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiTitleTreatmentType).includes(s.treatmentTypeKey as UiTitleTreatmentType) ||
          Object.values(UiTitleTreatmentType).includes(s.treatmentType as UiTitleTreatmentType)
      );
    case UiModuleType.UiRankedModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiRankedTreatmentType).includes(s.treatmentTypeKey as UiRankedTreatmentType) ||
          Object.values(UiRankedTreatmentType).includes(s.treatmentType as UiRankedTreatmentType)
      );
    case UiModuleType.UiTitleModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiTitleTreatmentType).includes(s.treatmentTypeKey as UiTitleTreatmentType) ||
          Object.values(UiTitleTreatmentType).includes(s.treatmentType as UiTitleTreatmentType)
      );
    case UiModuleType.UiTextModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiTextTreatmentType).includes(s.treatmentTypeKey as UiTextTreatmentType) ||
          Object.values(UiTextTreatmentType).includes(s.treatmentType as UiTextTreatmentType)
      );
    case UiModuleType.UiBasicOptionalModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiBasicOptionalTreatmentType).includes(s.treatmentTypeKey as UiBasicOptionalTreatmentType) ||
          Object.values(UiBasicOptionalTreatmentType).includes(s.treatmentType as UiBasicOptionalTreatmentType)
      );
    case UiModuleType.UiFeedsModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiFeedsTreatmentType).includes(s.treatmentTypeKey as UiFeedsTreatmentType) ||
          Object.values(UiFeedsTreatmentType).includes(s.treatmentType as UiFeedsTreatmentType)
      );
    case UiModuleType.UiCollectionModuleType:
      return treatmentTypeSettings.filter(
        (s) =>
          Object.values(UiCollectionTreatmentType).includes(s.treatmentTypeKey as UiCollectionTreatmentType) ||
          Object.values(UiCollectionTreatmentType).includes(s.treatmentType as UiCollectionTreatmentType)
      );
    default:
      return treatmentTypeSettings;
  }
}

/**
 * Returns the smallest non-negative integer not already used by a `${ttKey}-N` slotRef in
 * `siblingSlotRefs`. Used when changing a module's treatment type to avoid creating a duplicate
 * slotRef within the same group.
 */
export function nextFreeSlotRefIndex(ttKey: string, siblingSlotRefs: string[]): number {
  const prefix = `${ttKey}-`;
  const used = new Set(
    siblingSlotRefs
      .filter((ref) => ref.startsWith(prefix))
      .map((ref) => parseInt(ref.slice(prefix.length), 10))
      .filter((n) => !Number.isNaN(n))
  );
  return Array.from({ length: used.size + 1 }, (_, n) => n).find((n) => !used.has(n)) ?? 0;
}

export function useModuleTreatmentTypes({
  moduleFieldKey,
  onChangeNewModule,
  moduleContainer,
  subsetTreatmentTypeSettings,
  siblingSlotRefs
}: {
  moduleFieldKey: Exclude<keyof UiModule, '__typename'>;
  onChangeNewModule?: ((newModuleContainer: ModuleContainerUnion) => void) | null;
  moduleContainer?: ModuleContainerUnion;
  subsetTreatmentTypeSettings?: TreatmentTypeSetting[];
  siblingSlotRefs?: string[];
}) {
  const handleChangeTreatmentByValue = (value: string) => {
    const newModuleContainer = cloneDeep(moduleContainer);

    if (newModuleContainer?.attributes.pageModule?.uiModuleFields?.[moduleFieldKey]) {
      newModuleContainer.attributes.pageModule.uiModuleFields[moduleFieldKey]!.treatmentType = value;

      // prefill with feed queries
      if (moduleContainer && moduleFieldKey === 'feedsModule') {
        const newTreatmentTypeSettings = subsetTreatmentTypeSettings?.find(
          (setting) => setting.treatmentTypeKey === value || setting.treatmentType === value
        );

        const { min: effectiveMin } = getEffectiveItemLimits(
          newModuleContainer.attributes.pageModule,
          newTreatmentTypeSettings
        );
        if (
          newTreatmentTypeSettings &&
          effectiveMin !== undefined &&
          newModuleContainer.collection.length < effectiveMin
        ) {
          for (let i = newModuleContainer.collection.length; i < effectiveMin; i++) {
            const contentItem = cloneDeep(newModuleContainer.collection[0]);
            contentItem.attributes.id = uuidv4();
            newModuleContainer.collection.push(contentItem);
          }
        } else {
          newModuleContainer.collection = newModuleContainer.collection.slice(0, effectiveMin);
        }
      }
    }

    // For structure modules: slotRef encodes the ttKey that extractStructureFromRoot derives
    // on the backend, so it must change together with the treatment type. Kept outside the
    // uiModuleFields guard so it runs even when uiModuleFields hasn't been hydrated yet for
    // this field key (e.g. a freshly placed module whose fields were never written to root).
    if (newModuleContainer?.slotRef) {
      const slotRefMatch = newModuleContainer.slotRef.match(/^.*-(\d+)$/);
      if (slotRefMatch) {
        const idx = siblingSlotRefs ? nextFreeSlotRefIndex(value, siblingSlotRefs) : parseInt(slotRefMatch[1], 10);
        newModuleContainer.slotRef = `${value}-${idx}`;
      }
    }

    if (newModuleContainer) {
      onChangeNewModule?.(newModuleContainer);
    }
  };

  const handleChangeTreatment: ChangeEventHandler<HTMLInputElement> = (e) => {
    handleChangeTreatmentByValue(e.target.value);
  };

  const pageModule = moduleContainer?.attributes.pageModule as PageModule;
  const allowedTreatmentTypes =
    pageModule.uiModuleFields?.[moduleFieldKey]?.allowedTreatmentTypes ??
    ([pageModule.uiModuleFields?.[moduleFieldKey]?.treatmentType] as string[]);

  const allowedTreatmentTypeSettings = subsetTreatmentTypeSettings?.filter(
    (treatmentTypeSetting) =>
      allowedTreatmentTypes.includes(treatmentTypeSetting.treatmentType) ||
      allowedTreatmentTypes.includes(treatmentTypeSetting.treatmentTypeKey ?? '')
  );

  return {
    handleChangeTreatment,
    handleChangeTreatmentByValue,
    allowedTreatmentTypes,
    allowedTreatmentTypeSettings
  };
}

export const getContentTypes = (pageModule: PageModule): string[] => {
  let contentTypes = null;
  const keys = Object.keys(pageModule.uiModuleFields ?? {});
  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    const uiModuleFieldKey = key as keyof UiModule;
    const uiModule = pageModule.uiModuleFields?.[uiModuleFieldKey] as { contentTypes?: string[] } | null;
    if (uiModule?.contentTypes) {
      contentTypes = uiModule.contentTypes.map((contentType) => contentType.toLowerCase());
    }
  }
  return contentTypes ?? [];
};

const isQueryContentTypeAllowed = (allessehJsonQuery: string, allowedContentTypes: string[]): boolean => {
  const parsedQuery = JSON.parse(allessehJsonQuery) as AllessehContentQueryBody;
  if (parsedQuery.query) {
    const CONTENT_TYPE = 'ContentType';
    const contentTypeTerm = parsedQuery.query.and?.find((andRule: AllessehQueryRule) => {
      if ('term' in andRule) {
        return andRule.term.key === CONTENT_TYPE;
      }
      if ('terms' in andRule) {
        return andRule.terms.key === CONTENT_TYPE;
      }
      return false;
    });
    if (contentTypeTerm) {
      if ('terms' in contentTypeTerm) {
        return contentTypeTerm.terms.value.some((value: string) => allowedContentTypes.includes(value));
      }
      if ('term' in contentTypeTerm) {
        return allowedContentTypes.includes(contentTypeTerm.term.value);
      }
    }
    return allowedContentTypes.includes('article');
  }
  return false;
};

const getOriginContentType = (article: ArticleItem): PublicationSettingSearchableContentType => {
  if (article.attributes.product?.includes('Live Coverage')) {
    if (article.attributes.hosted_url?.includes('/card/')) {
      return PublicationSettingSearchableContentType.LiveCoverageCard;
    }
    return PublicationSettingSearchableContentType.LiveCoverageStack;
  }
  if (article.attributes.product?.includes('Interactive Media'))
    return PublicationSettingSearchableContentType.Tappable;
  if (article.attributes.product?.includes('WSJ.com Webstory')) return PublicationSettingSearchableContentType.Webstory;

  if (
    article.attributes.product === 'WSJ.com' ||
    article.attributes.product === 'WSJ Japanese' ||
    article.attributes.product === 'WSJ S Chinese' ||
    article.attributes.product === 'WSJ T Chinese'
  ) {
    const parsedArticle = safelyParseContent<AllessehContent>(article.content);
    if (
      parsedArticle.data.attributes.section_name === 'Opinion' ||
      parsedArticle.data.attributes.section_name === 'CN-Opinion' ||
      isJWSJOpinion(parsedArticle.data.attributes.section_type)
    ) {
      return PublicationSettingSearchableContentType.Opinion;
    }
  }

  if (article.attributes.product?.includes('Oxford Analytica')) {
    const parsedArticle = safelyParseContent<AllessehContent>(article.content);

    if (parsedArticle.data.attributes.do_not_archive === true) {
      return PublicationSettingSearchableContentType.OxanAnnouncements;
    }
  }

  if (article.attributes.product === 'Free Expression') {
    return PublicationSettingSearchableContentType.FreeExpression;
  }

  return article.attributes.originContentType as PublicationSettingSearchableContentType;
};

function coerceArticleContentType(article: ArticleItem): string | null {
  const parsed = safelyParseContent<AllessehContent>(article);
  const sectionName = parsed.data.attributes.section_name;
  if (sectionName) {
    const key = sectionName;
    const sectionOverride = SECTION_NAME_OVERRIDE[key];
    if (sectionOverride) return sectionOverride.toLowerCase();
  }

  // Fallback to your existing origin type
  const origin = getOriginContentType(article);
  return origin.toLowerCase();
}

function isArticle(item: ContentUnion): item is ArticleItem {
  return item.type === 'Article';
}

export const isContentTypeAllowedInModule = (module: ModuleContainer, contentItem: ContentItem): boolean => {
  const allowedContentTypes = getContentTypes(module.attributes.pageModule as PageModule);

  if (contentItem.type === 'Collection') {
    const collection = contentItem as ExternalCollectionItem;
    const { contentItems } = collection;
    const articles = contentItems.filter((item: ContentUnion): item is ArticleItem => isArticle(item));
    const query = contentItems.find((item: ContentUnion) => item.type === 'Query') as QueryItem | undefined;
    const hasArticles = articles.length > 0;
    const hasQuery = !!query;
    const isMixedCollection = hasArticles && hasQuery;
    const allessehJsonQuery = JSON.stringify(query?.attributes.query ?? '');

    if (isMixedCollection) {
      const isContentAllowed = articles.every((article) =>
        allowedContentTypes.includes(article.attributes.originContentType!)
      );
      if (!isContentAllowed) return false;

      return isQueryContentTypeAllowed(allessehJsonQuery, allowedContentTypes);
    }

    if (hasArticles) {
      return articles.every((article) => allowedContentTypes.includes(article.attributes.originContentType!));
    }

    if (hasQuery) {
      return isQueryContentTypeAllowed(allessehJsonQuery, allowedContentTypes);
    }
  }

  if (isArticle(contentItem)) {
    const t = coerceArticleContentType(contentItem);
    return t !== null && allowedContentTypes.includes(t);
  }

  if (contentItem.type === 'Query') {
    const query = contentItem as QueryItem;
    const allessehJsonQuery = JSON.stringify(query.attributes.query);

    return isQueryContentTypeAllowed(allessehJsonQuery, allowedContentTypes);
  }

  // allow manually pasted allesseh collection ids (added through Add collection ID button)
  return true;
};

export const isItemAllowedInModule = (
  module: ModuleContainer,
  contentItem: ContentItem,
  alertError: (message: string, options?: AlertOptions) => void
): boolean => {
  const isContentTypeAllowed = isContentTypeAllowedInModule(module, contentItem);
  if (!isContentTypeAllowed) {
    alertError('Content type is not allowed in the module.', { autoDismiss: true });
    return false;
  }

  return true;
};

export type AnyUiModule =
  | UiBasicModule
  | UiBasicOptionalModule
  | UiCollectionModule
  | UiFeedsModule
  | UiRankedModule
  | UiTextModule
  | UiTitleModule
  | UiTitlePinnedModule;

export const getModule = (pageModule: PageModule): AnyUiModule | null => {
  if (!pageModule.uiModuleType) return null;
  const property = getModuleFieldKey(pageModule.uiModuleType);
  return (pageModule.uiModuleFields?.[property] as AnyUiModule | null | undefined) ?? null;
};

export const handleChangePageModule = (
  fieldKey: 'title' | 'titleUrl' | 'subtitle' | 'subtitleUrl' | 'summaryPriority',
  pageModule: PageModule,
  text: string
) => {
  let value = text;
  if (['titleUrl', 'subtitleUrl'].includes(fieldKey)) {
    const firstChar = value[0];
    const isEmpty = (firstChar === '/' && !value[1]) || value === '';
    if (isEmpty) {
      value = '';
    } else if (firstChar !== '/') {
      value = `/${value}`;
    }
  }
  const newPageModule = cloneDeep(pageModule);
  const module = getModule(newPageModule);
  if (module) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    module[fieldKey] = value;
  }
  return newPageModule;
};

export const getNumTotalItemsBeforeIndex = (
  module: ModuleContainer | BannerModuleContainer | MultiZoneModuleContainer | OffPlatformListContainer,
  index: number
) => {
  let totalItems = 0;

  for (let i = 0; i < index; i++) {
    const item = module.collection[i];
    if (item.type === EntityType.Article) {
      const articleItem = item as ArticleItem;
      const parsedContent = safelyParseContent<AllessehContent>(articleItem);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!parsedContent.data?.attributes?.content_status) {
        totalItems += 1;
        continue;
      }
      const itemStatus = parsedContent.data.attributes.content_status;
      if (itemStatus !== 'embargo') {
        totalItems += 1;
      }
    } else if (item.type === EntityType.Collection) {
      const collectionItem = item as ExternalCollectionItem;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      totalItems += collectionItem.contentItems.length;
    } else if (item.type === EntityType.Query) {
      const queryItem = item as QueryItem;
      if (queryItem.attributes.isFeed) {
        totalItems += 1;
      } else {
        totalItems += queryItem.queryItems?.length ?? 0;
      }
    } else if (item.type === EntityType.LinkedItem) {
      totalItems += 1;
    }
  }

  return totalItems;
};

export const isFirstModuleInGroup = (module: object, groupCollection: object[]): boolean =>
  groupCollection[0] === module;

export const canRemoveModuleFromGroup = (
  groupConfig:
    | { removableTreatmentTypes?: string[] | null; minimumAllowed?: number; min?: number; canToggle?: boolean }
    | undefined,
  treatmentType: string | null,
  collectionLength: number
): boolean | undefined => {
  const minimum = groupConfig?.minimumAllowed ?? groupConfig?.min;

  if (minimum === 0 && collectionLength === 1) {
    return false;
  }

  if (groupConfig?.removableTreatmentTypes !== undefined && groupConfig.removableTreatmentTypes !== null) {
    const isRemovableType = treatmentType != null && groupConfig.removableTreatmentTypes.includes(treatmentType);
    const hasMoreThanMinimum = typeof minimum !== 'undefined' ? collectionLength > minimum : true;
    return isRemovableType && hasMoreThanMinimum;
  }

  if (typeof minimum !== 'undefined') {
    return collectionLength > minimum;
  }

  return undefined;
};
